import { TextInput, DateInput, SelectInput } from '../inputs';
import type { InvoiceFormData } from '../../types/invoice';

const PURPOSE_CODE_OPTIONS = [
  { value: '00', label: 'Original' },
  { value: '01', label: 'Cancellation' },
  { value: '05', label: 'Replace' },
];

const INVOICE_TYPE_OPTIONS = [
  { value: 'CN', label: 'Credit Note' },
  { value: 'DI', label: 'Debit Invoice' },
  { value: 'IN', label: 'Invoice' },
  { value: 'CR', label: 'Credit Invoice' },
];

const CURRENCY_CODE_OPTIONS = [
  { value: 'USD', label: 'US Dollar' },
  { value: 'EUR', label: 'Euro' },
  { value: 'GBP', label: 'British Pound' },
  { value: 'CAD', label: 'Canadian Dollar' },
  { value: 'MXN', label: 'Mexican Peso' },
];

interface InvoiceHeaderSectionProps {
  formData: InvoiceFormData;
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onDateChange: (qualifier: string, date: string) => void;
  onReferenceChange: (qualifier: string, id: string) => void;
}

export function InvoiceHeaderSection({ formData, errors, onChange, onDateChange, onReferenceChange }: InvoiceHeaderSectionProps) {
  // Extract ship date (DTM:011) from the dates array
  const shipDate = formData.dates.find(d => d.qualifier === '011');
  // Extract packing list ref (REF:PK) from the references array
  const packingListRef = formData.references.find(r => r.qualifier === 'PK');

  return (
    <div className="card-compact p-6">
      <h2 className="text-lg font-semibold mb-4 text-gray-900">Invoice Header</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SelectInput
          label="Purpose Code"
          value={formData.purposeCode}
          onChange={(value) => onChange('purposeCode', value)}
          options={PURPOSE_CODE_OPTIONS}
          error={errors.purposeCode}
        />

        <TextInput
          label="Invoice Number"
          value={formData.invoiceNumber}
          onChange={(value) => onChange('invoiceNumber', value)}
          error={errors.invoiceNumber}
          required
        />

        <DateInput
          label="Invoice Date"
          value={formData.invoiceDate}
          onChange={(value) => onChange('invoiceDate', value)}
          error={errors.invoiceDate}
          required
        />

        <SelectInput
          label="Invoice Type"
          value={formData.invoiceType}
          onChange={(value) => onChange('invoiceType', value)}
          options={INVOICE_TYPE_OPTIONS}
          error={errors.invoiceType}
        />

        <TextInput
          label="Purchase Order Number"
          value={formData.purchaseOrderNumber}
          onChange={(value) => onChange('purchaseOrderNumber', value)}
          error={errors.purchaseOrderNumber}
          required
        />

        <DateInput
          label="Purchase Order Date"
          value={formData.purchaseOrderDate}
          onChange={(value) => onChange('purchaseOrderDate', value)}
          error={errors.purchaseOrderDate}
        />

        <SelectInput
          label="Currency Code"
          value={formData.currencyCode}
          onChange={(value) => onChange('currencyCode', value)}
          options={CURRENCY_CODE_OPTIONS}
          error={errors.currencyCode}
          required
        />

        <DateInput
          label="Ship Date (DTM:011)"
          value={shipDate?.date || ''}
          onChange={(value) => onDateChange('011', value)}
          error={errors.dates_011}
          required
        />

        <TextInput
          label="Packing List # (REF:PK)"
          value={packingListRef?.id || ''}
          onChange={(value) => onReferenceChange('PK', value)}
          error={errors.references_PK}
          required
          placeholder="e.g., PL-12345"
        />

        <TextInput
          label="Payment Terms"
          value={formData.paymentTerms}
          onChange={(value) => onChange('paymentTerms', value)}
          error={errors.paymentTerms}
          placeholder="e.g., Net 30"
        />

        <div className="md:col-span-2">
          <TextInput
            label="Payment Terms Description"
            value={formData.paymentTermsDescription || ''}
            onChange={(value) => onChange('paymentTermsDescription', value)}
            error={errors.paymentTermsDescription}
            placeholder="e.g., Payment due within 30 days"
          />
        </div>
      </div>
    </div>
  );
}
